import { nowUtcIso } from '@/lib/dates'
import type {
  CarrierLockStatus,
  Device,
  DeviceImei,
  MdmStatus,
  PtaStatus,
  Purchase,
} from '@/offline/types'

export interface DeviceEditForm {
  model: string
  category_id: string
  brand_code: string | null
  storage: string | null
  ram: string | null
  condition: string | null
  color: string | null
  pta_status: PtaStatus
  carrier_lock_status: CarrierLockStatus | null
  mdm_status: MdmStatus | null
  imeis: Array<{ id: string; imei_normalized: string; imei_pta_status: PtaStatus }>
  cost_paisa: number
  direct_cost_paisa: number
}

export interface DeviceUpdateInput {
  workspace_id: string
  device: Device
  imeis: DeviceImei[]
  purchase?: Purchase
  form: DeviceEditForm
}

export interface BuiltDeviceUpdate {
  device: Device
  imeis: DeviceImei[]
  purchase?: Purchase
  expectedRevision: number
  payload: Record<string, unknown>
}

/**
 * Build an EDIT as an update of the SAME device/purchase UUIDs (plan section 7):
 * never creates a new handset. Bumps revision locally and sends expected_revision
 * so the server can detect concurrent-edit conflicts instead of last-write-wins.
 */
export function buildDeviceUpdate(input: DeviceUpdateInput): BuiltDeviceUpdate {
  const now = nowUtcIso()
  const isApple = input.form.brand_code === 'apple'
  const expectedRevision = input.device.revision

  const device: Device = {
    ...input.device,
    model: input.form.model.trim(),
    category_id: input.form.category_id,
    brand_code: input.form.brand_code,
    storage: input.form.storage,
    ram: input.form.ram,
    condition: input.form.condition,
    color: input.form.color,
    pta_status: input.form.pta_status,
    carrier_lock_status: isApple ? input.form.carrier_lock_status : null,
    mdm_status: isApple ? input.form.mdm_status : null,
    revision: input.device.revision + 1,
    updated_at: now,
  }

  const imeis = input.imeis.map((im) => {
    const f = input.form.imeis.find((x) => x.id === im.id)
    return f
      ? { ...im, imei_normalized: f.imei_normalized, imei_pta_status: f.imei_pta_status, updated_at: now }
      : im
  })

  const purchase = input.purchase
    ? {
        ...input.purchase,
        cost_paisa: input.form.cost_paisa,
        direct_cost_paisa: input.form.direct_cost_paisa,
        updated_at: now,
      }
    : undefined

  const payload: Record<string, unknown> = {
    model: device.model,
    category_id: device.category_id,
    brand_code: device.brand_code,
    storage: device.storage,
    ram: device.ram,
    condition: device.condition,
    color: device.color,
    pta_status: device.pta_status,
    carrier_lock_status: device.carrier_lock_status,
    mdm_status: device.mdm_status,
    imeis: input.form.imeis,
    purchase: {
      cost_paisa: input.form.cost_paisa,
      direct_cost_paisa: input.form.direct_cost_paisa,
    },
  }

  return { device, imeis, purchase, expectedRevision, payload }
}
